/**
 * Section membership: bounding-box intersection, adoption candidates,
 * parent picking, and position conversion for React Flow subflows.
 */

import type { InternalNode, Node } from "@xyflow/react";
import { isSectionMarkedCollapsed } from "./sectionLegacySizing";

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type GetInternalNode = (id: string) => InternalNode | undefined;

const EXCLUDED_NODE_TYPES = new Set([
  "chatSession",
  "canvasConnector",
  "canvasDrawing",
  "commentMarker",
]);

export function getNodeWidth(node: Node): number {
  const style = node.style as { width?: number | string } | undefined;
  const styleW =
    typeof style?.width === "number"
      ? style.width
      : typeof style?.width === "string"
        ? parseFloat(style.width)
        : NaN;
  return (
    node.measured?.width ??
    node.width ??
    (Number.isFinite(styleW) ? styleW : 0) ??
    0
  );
}

export function getNodeHeight(node: Node): number {
  const style = node.style as { height?: number | string } | undefined;
  const styleH =
    typeof style?.height === "number"
      ? style.height
      : typeof style?.height === "string"
        ? parseFloat(style.height)
        : NaN;
  return (
    node.measured?.height ??
    node.height ??
    (Number.isFinite(styleH) ? styleH : 0) ??
    0
  );
}

function readPositionAbsolute(node: Node): { x: number; y: number } | null {
  const internals = (
    node as Node & {
      internals?: { positionAbsolute?: { x: number; y: number } };
    }
  ).internals;
  const abs = internals?.positionAbsolute;
  if (
    abs &&
    typeof abs.x === "number" &&
    typeof abs.y === "number" &&
    Number.isFinite(abs.x) &&
    Number.isFinite(abs.y)
  ) {
    return { x: abs.x, y: abs.y };
  }
  return null;
}

/** Flow-space axis-aligned bounds for a node. */
export function getNodeAbsoluteBounds(
  node: Node,
  getInternalNode?: GetInternalNode,
  nodesById?: Map<string, Node>,
): Bounds {
  const internal = getInternalNode?.(node.id);
  const fromInternal = internal?.internals?.positionAbsolute;
  let x: number;
  let y: number;

  if (
    fromInternal &&
    typeof fromInternal.x === "number" &&
    typeof fromInternal.y === "number"
  ) {
    x = fromInternal.x;
    y = fromInternal.y;
  } else {
    const abs = readPositionAbsolute(node);
    if (abs) {
      x = abs.x;
      y = abs.y;
    } else {
      x = node.position.x;
      y = node.position.y;
      let cur: Node | undefined = node;
      while (cur?.parentId && nodesById) {
        const parent = nodesById.get(cur.parentId);
        if (!parent) break;
        x += parent.position.x;
        y += parent.position.y;
        cur = parent;
      }
    }
  }

  const width = getNodeWidth(node);
  const height = getNodeHeight(node);

  return { x, y, width, height };
}

export function boundsFromRect(
  position: { x: number; y: number },
  width: number,
  height: number,
): Bounds {
  return { x: position.x, y: position.y, width, height };
}

/** True when rectangles overlap with any positive area (partial overlap counts). */
export function rectsIntersect(a: Bounds, b: Bounds): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/** True when `inner` is entirely inside `outer` (edges may touch). */
export function rectFullyContains(outer: Bounds, inner: Bounds): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

/** Chrome and projections never adopt, and are never adopted. Untyped counts. */
export function isExcludedNodeType(type: string | undefined): boolean {
  return !type || EXCLUDED_NODE_TYPES.has(type);
}

export function isExcludedFromSectionMembership(
  node: Node,
  excludeSectionId?: string,
): boolean {
  if (excludeSectionId && node.id === excludeSectionId) return true;
  return isExcludedNodeType(node.type);
}

export function isSectionNode(node: Node): boolean {
  return node.type === "section";
}

/** Bounds used for intersection, adoption, and enclosure checks. */
export function getSectionMembershipBounds(
  section: Node,
  getInternalNode?: GetInternalNode,
  nodesById?: Map<string, Node>,
): Bounds {
  return getNodeAbsoluteBounds(section, getInternalNode, nodesById);
}

export function getDescendantIds(nodeId: string, nodes: Node[]): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const n of nodes) {
    if (!n.parentId) continue;
    const list = byParent.get(n.parentId) ?? [];
    list.push(n.id);
    byParent.set(n.parentId, list);
  }

  const result = new Set<string>();
  const stack = [...(byParent.get(nodeId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (result.has(id)) continue;
    result.add(id);
    for (const childId of byParent.get(id) ?? []) {
      stack.push(childId);
    }
  }
  return result;
}

/** Block attaching `childId` under `parentSectionId` if that would create a cycle. */
export function wouldCreateParentCycle(
  childId: string,
  parentSectionId: string,
  nodes: Node[],
): boolean {
  if (childId === parentSectionId) return true;
  const descendants = getDescendantIds(childId, nodes);
  return descendants.has(parentSectionId);
}

export function sectionDepth(
  sectionId: string,
  nodesById: Map<string, Node>,
): number {
  let depth = 0;
  let cur = nodesById.get(sectionId);
  while (cur?.parentId) {
    const parent = nodesById.get(cur.parentId);
    if (!parent || !isSectionNode(parent)) break;
    depth += 1;
    cur = parent;
  }
  return depth;
}

function boundsArea(b: Bounds): number {
  return b.width * b.height;
}

function sectionArea(
  section: Node,
  getInternalNode?: GetInternalNode,
  nodesById?: Map<string, Node>,
): number {
  return boundsArea(
    getSectionMembershipBounds(section, getInternalNode, nodesById),
  );
}

/**
 * Smallest section (by area) that fully contains `dragged` and is larger than it.
 * Used when dragging a section into a parent.
 */
export function pickEnclosingParentSection(
  draggedSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Node | null {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const draggedBounds = getSectionMembershipBounds(
    draggedSection,
    getInternalNode,
    nodesById,
  );
  const draggedArea = boundsArea(draggedBounds);

  let best: Node | null = null;
  let bestArea = Infinity;

  for (const other of nodes) {
    if (!isSectionNode(other) || other.id === draggedSection.id) continue;
    const otherBounds = getSectionMembershipBounds(
      other,
      getInternalNode,
      nodesById,
    );
    const otherArea = boundsArea(otherBounds);
    if (otherArea <= draggedArea) continue;
    if (!rectFullyContains(otherBounds, draggedBounds)) continue;
    if (wouldCreateParentCycle(draggedSection.id, other.id, nodes)) continue;
    if (otherArea < bestArea) {
      best = other;
      bestArea = otherArea;
    }
  }

  return best;
}

/** Smaller sections fully enclosed by `host` (top-level among candidates). */
export function getSmallerSectionsFullyEnclosedBy(
  hostSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Node[] {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const hostBounds = getSectionMembershipBounds(
    hostSection,
    getInternalNode,
    nodesById,
  );
  const hostArea = boundsArea(hostBounds);
  const enclosed: Node[] = [];

  for (const other of nodes) {
    if (!isSectionNode(other) || other.id === hostSection.id) continue;
    const otherBounds = getSectionMembershipBounds(
      other,
      getInternalNode,
      nodesById,
    );
    if (boundsArea(otherBounds) >= hostArea) continue;
    if (!rectFullyContains(hostBounds, otherBounds)) continue;
    if (wouldCreateParentCycle(other.id, hostSection.id, nodes)) continue;
    enclosed.push(other);
  }

  const enclosedIds = new Set(enclosed.map((n) => n.id));
  return enclosed.filter((node) => {
    for (const other of enclosed) {
      if (other.id === node.id) continue;
      if (isDescendantOf(node.id, other.id, nodesById)) return false;
    }
    if (node.parentId && enclosedIds.has(node.parentId)) return false;
    return true;
  });
}

/** Among section nodes, pick the deepest nested; tie-break by smallest area. */
export function pickTargetSection(
  intersectingSections: Node[],
  nodes: Node[],
): Node | null {
  if (intersectingSections.length === 0) return null;
  const nodesById = new Map(nodes.map((n) => [n.id, n]));

  let best: Node | null = null;
  let bestDepth = -1;
  let bestArea = Infinity;

  for (const section of intersectingSections) {
    const depth = sectionDepth(section.id, nodesById);
    const area = boundsArea(
      getSectionMembershipBounds(section, undefined, nodesById),
    );
    if (depth > bestDepth || (depth === bestDepth && area < bestArea)) {
      best = section;
      bestDepth = depth;
      bestArea = area;
    }
  }

  return best;
}

function isDescendantOf(
  nodeId: string,
  ancestorId: string,
  nodesById: Map<string, Node>,
): boolean {
  let cur = nodesById.get(nodeId);
  while (cur?.parentId) {
    if (cur.parentId === ancestorId) return true;
    cur = nodesById.get(cur.parentId);
  }
  return false;
}

/**
 * Nodes that should become children of a section from a bounds rect.
 * Drops descendants when an ancestor is also a candidate (nested section moves as one).
 */
export function getAdoptionCandidates(
  nodes: Node[],
  sectionBounds: Bounds,
  options: {
    excludeSectionId: string;
    getInternalNode?: GetInternalNode;
  },
): Node[] {
  const { excludeSectionId, getInternalNode } = options;
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const intersecting: Node[] = [];

  for (const node of nodes) {
    if (isExcludedFromSectionMembership(node, excludeSectionId)) continue;
    const bounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
    if (rectsIntersect(bounds, sectionBounds)) {
      intersecting.push(node);
    }
  }

  const candidateIds = new Set(intersecting.map((n) => n.id));

  return intersecting.filter((node) => {
    for (const other of intersecting) {
      if (other.id === node.id) continue;
      if (isDescendantOf(node.id, other.id, nodesById)) return false;
    }
    if (node.parentId && candidateIds.has(node.parentId)) {
      return false;
    }
    return true;
  });
}

/**
 * Refines intersection-based adoption candidates: non-section nodes pass through;
 * section nodes require full enclosure and smaller area than the host. Also adds
 * fully enclosed smaller sections that may not appear in the intersecting pass.
 */
export function refineAdoptionCandidatesForSectionNesting(
  candidates: Node[],
  hostSection: Node,
  sectionBounds: Bounds,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Node[] {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const filtered = candidates.filter((node) => {
    if (!isSectionNode(node)) return true;
    const nodeBounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
    return (
      sectionArea(hostSection, getInternalNode, nodesById) >
        boundsArea(nodeBounds) && rectFullyContains(sectionBounds, nodeBounds)
    );
  });
  const enclosedSections = getSmallerSectionsFullyEnclosedBy(
    hostSection,
    nodes,
    getInternalNode,
  );
  return [
    ...filtered,
    ...enclosedSections.filter((s) => !filtered.some((c) => c.id === s.id)),
  ];
}

export function toRelativePosition(
  absolutePosition: { x: number; y: number },
  parentBounds: Bounds,
): { x: number; y: number } {
  return {
    x: absolutePosition.x - parentBounds.x,
    y: absolutePosition.y - parentBounds.y,
  };
}

export function toAbsolutePosition(
  relativePosition: { x: number; y: number },
  parentBounds: Bounds,
): { x: number; y: number } {
  return {
    x: relativePosition.x + parentBounds.x,
    y: relativePosition.y + parentBounds.y,
  };
}

/** Absolute top-left for attach from a node's current relative position + parent chain. */
export function nodeToAbsolutePosition(
  node: Node,
  getInternalNode?: GetInternalNode,
  nodesById?: Map<string, Node>,
): { x: number; y: number } {
  const bounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
  return { x: bounds.x, y: bounds.y };
}

export function buildParentUpdatesForAdoption(
  candidates: Node[],
  parentSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Array<{
  nodeId: string;
  parentId: string;
  position: { x: number; y: number };
}> {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const parentBounds = isSectionNode(parentSection)
    ? getSectionMembershipBounds(parentSection, getInternalNode, nodesById)
    : getNodeAbsoluteBounds(parentSection, getInternalNode, nodesById);

  const updates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }> = [];

  for (const node of candidates) {
    if (node.id === parentSection.id) continue;
    if (wouldCreateParentCycle(node.id, parentSection.id, nodes)) continue;
    if (node.parentId === parentSection.id) continue;

    const abs = nodeToAbsolutePosition(node, getInternalNode, nodesById);
    updates.push({
      nodeId: node.id,
      parentId: parentSection.id,
      position: toRelativePosition(abs, parentBounds),
    });
  }

  return updates;
}

export function buildDetachUpdates(
  nodeIds: string[],
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Array<{
  nodeId: string;
  parentId: null;
  position: { x: number; y: number };
}> {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const updates: Array<{
    nodeId: string;
    parentId: null;
    position: { x: number; y: number };
  }> = [];

  for (const nodeId of nodeIds) {
    const node = nodesById.get(nodeId);
    if (!node?.parentId) continue;
    const parent = nodesById.get(node.parentId);
    if (!parent) continue;

    const parentBounds = getNodeAbsoluteBounds(
      parent,
      getInternalNode,
      nodesById,
    );
    const abs = toAbsolutePosition(node.position, parentBounds);
    updates.push({
      nodeId: node.id,
      parentId: null,
      position: abs,
    });
  }

  return updates;
}

/**
 * Detach updates for children of sections in `nodeIdsToRemove` that are not
 * themselves being removed. Call before deleting sections so orphans keep
 * canvas-absolute positions instead of stale parent-relative coords.
 */
export function buildDetachUpdatesForSectionRemovals(
  nodeIdsToRemove: string[],
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Array<{
  nodeId: string;
  parentId: null;
  position: { x: number; y: number };
}> {
  const removeSet = new Set(nodeIdsToRemove);
  const sectionIdsBeingRemoved = new Set(
    nodes
      .filter((n) => removeSet.has(n.id) && isSectionNode(n))
      .map((n) => n.id),
  );
  if (sectionIdsBeingRemoved.size === 0) {
    return [];
  }

  const childIdsToDetach: string[] = [];
  for (const node of nodes) {
    if (!node.parentId) continue;
    if (!sectionIdsBeingRemoved.has(node.parentId)) continue;
    if (removeSet.has(node.id)) continue;
    childIdsToDetach.push(node.id);
  }

  return buildDetachUpdates(childIdsToDetach, nodes, getInternalNode);
}

/** True when node bounds no longer intersect parent section bounds. */
export function shouldDetachFromParent(
  node: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): boolean {
  if (!node.parentId) return false;
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const parent = nodesById.get(node.parentId);
  if (!parent || !isSectionNode(parent)) return false;
  if (isSectionMarkedCollapsed(parent.data)) return false;

  const nodeBounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
  const parentBounds = isSectionNode(parent)
    ? getSectionMembershipBounds(parent, getInternalNode, nodesById)
    : getNodeAbsoluteBounds(parent, getInternalNode, nodesById);

  if (isSectionNode(node) && isSectionNode(parent)) {
    return !rectFullyContains(parentBounds, nodeBounds);
  }
  return !rectsIntersect(nodeBounds, parentBounds);
}

/** Walk `parentId` until a section ancestor is found. */
export function resolveOwningSectionId(
  startParentId: string | undefined,
  sectionIds: Set<string>,
  nodesById: Map<string, Node>,
): string | undefined {
  let currentId = startParentId;
  const visited = new Set<string>();
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    if (sectionIds.has(currentId)) return currentId;
    currentId = nodesById.get(currentId)?.parentId;
  }
  return undefined;
}

/**
 * Section that owns a node in the explorer: `parentId` chain first, then
 * deepest intersecting section by bounds (matches canvas adoption when
 * `parentId` was never persisted).
 */
/**
 * When placing a new root-level node, parent it under the deepest section
 * whose bounds intersect the node's placement rect (if any).
 */
export function attachNodeToSectionIfApplicable(args: {
  node: Node;
  nodes: Node[];
  getInternalNode?: GetInternalNode;
}): Node {
  const { node, nodes, getInternalNode } = args;
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const bounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
  const intersecting = findIntersectingSections(bounds, nodes, getInternalNode);
  const target = pickTargetSection(intersecting, nodes);
  if (!target) return node;

  const parentBounds = getSectionMembershipBounds(
    target,
    getInternalNode,
    nodesById,
  );
  return {
    ...node,
    parentId: target.id,
    position: toRelativePosition({ x: bounds.x, y: bounds.y }, parentBounds),
  };
}

export function resolveExplorerSectionForNode(
  node: Node,
  sectionNodes: Node[],
  allNodes: Node[],
  nodesById: Map<string, Node>,
): string | undefined {
  const sectionIds = new Set(sectionNodes.map((s) => s.id));
  const fromParent = resolveOwningSectionId(
    node.parentId,
    sectionIds,
    nodesById,
  );
  if (fromParent) return fromParent;

  const bounds = getNodeAbsoluteBounds(node, undefined, nodesById);
  const intersecting = findIntersectingSections(bounds, allNodes, undefined);
  const picked = pickTargetSection(intersecting, allNodes);
  return picked?.id;
}

export function findIntersectingSections(
  targetBounds: Bounds,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
  excludeSectionId?: string,
): Node[] {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  return nodes.filter((n) => {
    if (!isSectionNode(n)) return false;
    if (excludeSectionId && n.id === excludeSectionId) return false;
    const bounds = isSectionNode(n)
      ? getSectionMembershipBounds(n, getInternalNode, nodesById)
      : getNodeAbsoluteBounds(n, getInternalNode, nodesById);
    return rectsIntersect(bounds, targetBounds);
  });
}

/**
 * When `targetSection` nests under `formerParentId`, re-parent that parent's
 * direct children that fall inside `targetSection` (e.g. nodes already in the
 * outer section move under a newly nested inner section).
 */
export function getDirectChildReparentUpdatesIntoSection(
  targetSection: Node,
  formerParentId: string,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Array<{
  nodeId: string;
  parentId: string;
  position: { x: number; y: number };
}> {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const targetBounds = getSectionMembershipBounds(
    targetSection,
    getInternalNode,
    nodesById,
  );
  const targetArea = sectionArea(targetSection, getInternalNode, nodesById);
  const candidates: Node[] = [];

  for (const node of nodes) {
    if (node.id === targetSection.id) continue;
    if (isExcludedFromSectionMembership(node, targetSection.id)) continue;
    if (node.parentId !== formerParentId) continue;

    const nodeBounds = isSectionNode(node)
      ? getSectionMembershipBounds(node, getInternalNode, nodesById)
      : getNodeAbsoluteBounds(node, getInternalNode, nodesById);

    if (isSectionNode(node)) {
      if (boundsArea(nodeBounds) >= targetArea) continue;
      if (!rectFullyContains(targetBounds, nodeBounds)) continue;
      if (wouldCreateParentCycle(node.id, targetSection.id, nodes)) continue;
    } else if (!rectsIntersect(nodeBounds, targetBounds)) {
      continue;
    }

    candidates.push(node);
  }

  return buildParentUpdatesForAdoption(
    candidates,
    targetSection,
    nodes,
    getInternalNode,
  );
}

/** Preview + commit for section-on-section drag (full enclosure, larger = parent). */
export function getSectionDragReparentState(
  draggedSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): {
  adopt: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
  detach: Array<{
    nodeId: string;
    parentId: null;
    position: { x: number; y: number };
  }>;
  previewMemberIds: Set<string>;
  previewTargetSectionId?: string;
} {
  const adopt: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }> = [];
  const previewMemberIds = new Set<string>();
  let previewTargetSectionId: string | undefined;

  const parentTarget = pickEnclosingParentSection(
    draggedSection,
    nodes,
    getInternalNode,
  );
  if (parentTarget && parentTarget.id !== draggedSection.parentId) {
    adopt.push(
      ...buildParentUpdatesForAdoption(
        [draggedSection],
        parentTarget,
        nodes,
        getInternalNode,
      ),
    );
    const stolen = getDirectChildReparentUpdatesIntoSection(
      draggedSection,
      parentTarget.id,
      nodes,
      getInternalNode,
    );
    adopt.push(...stolen);
    previewTargetSectionId = draggedSection.id;
    previewMemberIds.add(draggedSection.id);
    for (const u of stolen) {
      previewMemberIds.add(u.nodeId);
    }
  }

  const childSections = getSmallerSectionsFullyEnclosedBy(
    draggedSection,
    nodes,
    getInternalNode,
  );
  if (childSections.length > 0) {
    for (const child of childSections) {
      adopt.push(
        ...buildParentUpdatesForAdoption(
          [child],
          draggedSection,
          nodes,
          getInternalNode,
        ),
      );
      const stolen = getDirectChildReparentUpdatesIntoSection(
        child,
        draggedSection.id,
        nodes,
        getInternalNode,
      );
      adopt.push(...stolen);
      previewMemberIds.add(child.id);
      for (const u of stolen) {
        previewMemberIds.add(u.nodeId);
      }
    }
    previewTargetSectionId = draggedSection.id;
  }

  let detach: Array<{
    nodeId: string;
    parentId: null;
    position: { x: number; y: number };
  }> = [];

  if (
    !parentTarget &&
    draggedSection.parentId &&
    shouldDetachFromParent(draggedSection, nodes, getInternalNode)
  ) {
    detach = buildDetachUpdates([draggedSection.id], nodes, getInternalNode);
    previewMemberIds.add(draggedSection.id);
  }

  return { adopt, detach, previewMemberIds, previewTargetSectionId };
}

/** Reparent a dragged section (full enclosure, larger section is always parent). */
export function getSectionDragReparentUpdates(
  draggedSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): {
  adopt: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
  detach: Array<{
    nodeId: string;
    parentId: null;
    position: { x: number; y: number };
  }>;
} {
  const { adopt, detach } = getSectionDragReparentState(
    draggedSection,
    nodes,
    getInternalNode,
  );
  return { adopt, detach };
}

export type SectionParentUpdate = {
  nodeId: string;
  parentId: string | null;
  position: { x: number; y: number };
};

/** Last update per node wins; adopt beats detach for the same node. */
export function dedupeSectionParentUpdates(
  updates: SectionParentUpdate[],
): SectionParentUpdate[] {
  const byId = new Map<string, SectionParentUpdate>();
  for (const update of updates) {
    const prev = byId.get(update.nodeId);
    if (!prev) {
      byId.set(update.nodeId, update);
      continue;
    }
    if (prev.parentId === null && update.parentId !== null) {
      byId.set(update.nodeId, update);
      continue;
    }
    if (prev.parentId !== null && update.parentId === null) {
      continue;
    }
    byId.set(update.nodeId, update);
  }
  return [...byId.values()];
}

/** Nodes that would be newly adopted if the section bounds were committed now. */
export function getSectionAdoptionPreviewMemberIds(
  section: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Set<string> {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const sectionBounds = getSectionMembershipBounds(
    section,
    getInternalNode,
    nodesById,
  );
  const allCandidates = refineAdoptionCandidatesForSectionNesting(
    getAdoptionCandidates(nodes, sectionBounds, {
      excludeSectionId: section.id,
      getInternalNode,
    }),
    section,
    sectionBounds,
    nodes,
    getInternalNode,
  );
  const adopt = buildParentUpdatesForAdoption(
    allCandidates,
    section,
    nodes,
    getInternalNode,
  );
  return new Set(adopt.map((u) => u.nodeId));
}

/** Synthetic section id for placement preview (not persisted). */
export const SECTION_PLACEMENT_PREVIEW_ID = "__section-placement-preview__";

/**
 * Nodes that would be adopted if a section were committed at `ghost` bounds.
 * Matches `computeAdoptionUpdatesForNewSection` / post-placement adoption.
 */
export function getSectionPlacementPreviewMemberIds(
  ghost: {
    position: { x: number; y: number };
    width: number;
    height: number;
  },
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): Set<string> {
  const sectionBounds = boundsFromRect(
    ghost.position,
    ghost.width,
    ghost.height,
  );
  const hostSection: Node = {
    id: SECTION_PLACEMENT_PREVIEW_ID,
    type: "section",
    position: ghost.position,
    width: ghost.width,
    height: ghost.height,
    data: {},
  };
  const candidates = refineAdoptionCandidatesForSectionNesting(
    getAdoptionCandidates(nodes, sectionBounds, {
      excludeSectionId: SECTION_PLACEMENT_PREVIEW_ID,
      getInternalNode,
    }),
    hostSection,
    sectionBounds,
    nodes,
    getInternalNode,
  );
  return new Set(
    buildParentUpdatesForAdoption(
      candidates,
      hostSection,
      nodes,
      getInternalNode,
    ).map((u) => u.nodeId),
  );
}

/**
 * Reconcile section membership: adopt intersecting nodes, detach children
 * that no longer overlap (partial overlap rules).
 */
export type ComputeSectionMembershipOptions = {
  /** Section-on-section nesting is handled elsewhere (e.g. section drag reparent). */
  skipSectionAdoptions?: boolean;
};

export function computeSectionMembershipChanges(
  section: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
  options?: ComputeSectionMembershipOptions,
): {
  adopt: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
  detach: Array<{
    nodeId: string;
    parentId: null;
    position: { x: number; y: number };
  }>;
} {
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const sectionBounds = getSectionMembershipBounds(
    section,
    getInternalNode,
    nodesById,
  );

  let allAdoptCandidates = refineAdoptionCandidatesForSectionNesting(
    getAdoptionCandidates(nodes, sectionBounds, {
      excludeSectionId: section.id,
      getInternalNode,
    }),
    section,
    sectionBounds,
    nodes,
    getInternalNode,
  );

  if (options?.skipSectionAdoptions) {
    allAdoptCandidates = allAdoptCandidates.filter((n) => !isSectionNode(n));
  }

  const adopt = buildParentUpdatesForAdoption(
    allAdoptCandidates,
    section,
    nodes,
    getInternalNode,
  );

  if (section.parentId) {
    const parent = nodesById.get(section.parentId);
    if (parent && isSectionNode(parent)) {
      adopt.push(
        ...getDirectChildReparentUpdatesIntoSection(
          section,
          section.parentId,
          nodes,
          getInternalNode,
        ),
      );
    }
  }

  const adoptIds = new Set(adopt.map((u) => u.nodeId));
  const detachIds: string[] = [];

  for (const node of nodes) {
    if (node.parentId !== section.id) continue;
    if (adoptIds.has(node.id)) continue;
    if (isExcludedFromSectionMembership(node)) continue;
    if (shouldDetachFromParent(node, nodes, getInternalNode)) {
      detachIds.push(node.id);
    }
  }

  const detach = buildDetachUpdates(detachIds, nodes, getInternalNode);

  return { adopt, detach };
}

/**
 * Parent updates when a section drag stops: section-on-section reparenting plus
 * non-section adopt/detach, without duplicating enclosed-section adoption.
 */
export function getSectionDragStopParentUpdates(
  draggedSection: Node,
  nodes: Node[],
  getInternalNode?: GetInternalNode,
): SectionParentUpdate[] {
  const reparent = getSectionDragReparentUpdates(
    draggedSection,
    nodes,
    getInternalNode,
  );
  const updates: SectionParentUpdate[] = [
    ...reparent.adopt,
    ...reparent.detach,
  ];

  const membership = computeSectionMembershipChanges(
    draggedSection,
    nodes,
    getInternalNode,
    { skipSectionAdoptions: true },
  );
  updates.push(...membership.adopt, ...membership.detach);

  return dedupeSectionParentUpdates(updates);
}

/**
 * Explorer drop reparent: preserve each node's canvas-absolute position and
 * only change `parentId` (relative coords when adopting into a section).
 */
export function buildExplorerReparentUpdates(args: {
  nodeIds: string[];
  getNode: (id: string) => Node | undefined;
  getInternalNode: GetInternalNode;
  targetParentSectionId: string | null;
  /** Section absolute origin; required when `targetParentSectionId` is set. */
  targetSectionAbsPos?: { x: number; y: number };
  snap: (value: number, step: number) => number;
  snapX: number;
  snapY: number;
}): SectionParentUpdate[] {
  const updates: SectionParentUpdate[] = [];
  for (const nodeId of args.nodeIds) {
    const node = args.getNode(nodeId);
    if (!node) continue;
    const currentParent = node.parentId ?? null;
    if (currentParent === args.targetParentSectionId) continue;

    const internal = args.getInternalNode(nodeId);
    const absPos = internal?.internals.positionAbsolute ?? node.position;

    if (args.targetParentSectionId === null) {
      updates.push({
        nodeId,
        parentId: null,
        position: {
          x: args.snap(absPos.x, args.snapX),
          y: args.snap(absPos.y, args.snapY),
        },
      });
      continue;
    }

    if (!args.targetSectionAbsPos) continue;

    updates.push({
      nodeId,
      parentId: args.targetParentSectionId,
      position: {
        x: args.snap(absPos.x - args.targetSectionAbsPos.x, args.snapX),
        y: args.snap(absPos.y - args.targetSectionAbsPos.y, args.snapY),
      },
    });
  }
  return updates;
}
